import { PromiseResponse } from "../../types/shared";
import { supabase } from "../supabase/client";

/**
 * API functions for lens testing functionality using Supabase Edge Functions
 */

interface PromptTemplate {
  title: string;
  provider: string;
  model: string;
  tokens: number;
  temperature: number;
  tool_schema_name: string;
  input_variables: string[];
  content: string;
}

interface ToolSchema {
  name: string;
  description: string;
  input_schema: {
    type: string;
    properties: Record<string, any>;
    required: string[];
  };
}

interface TestLensParams {
  testLens: string;
  transcript: string;
  callDataRecord: any;
  companyName: string;
  companyDescription: string;
  model: string;
  tokens: number;
  temperature: number;
}

interface LensData {
  industry: string;
  promptTemplate: PromptTemplate;
  toolSchema: ToolSchema;
  currentLens: string;
  protocolIndex: string;
  customLensGroups: string;
}

/**
 * Fetches lens data including prompt template, tool schema, and current lens
 */
const fetchLensData = async (tenantId: string, lensName?: string): PromiseResponse<LensData> => {
  const { data, error } = await supabase.functions.invoke("get-lens-data", {
    method: "POST",
    body: { tenantId, lensName }
  });

  if (error) {
    return { success: false, error: error.message, data: null };
  }

  return { success: true, data, error: null };
};

/**
 * Fetches individual lens content by full path
 */
const fetchLensContent = async (tenantId: string, fullPath: string): PromiseResponse<string> => {
  const { data, error } = await supabase.functions.invoke("get-lens-data", {
    method: "POST",
    body: {
      tenantId,
      fullPath
    }
  });

  if (error) {
    return { success: false, error: error.message, data: null };
  }

  return { success: true, data: data.currentLens || "", error: null };
};

/**
 * Tests a lens using the Supabase Edge Function
 */
const testLensWithClaude = async (params: TestLensParams): PromiseResponse<any> => {
  const { data, error } = await supabase.functions.invoke("test-lens", {
    method: "POST",
    body: params
  });

  if (error) {
    return { success: false, error: error.message, data: null };
  }

  if (!data.success) {
    return { success: false, error: data.error || "Failed to test lens", data: null };
  }

  return { success: true, data: data.data, error: null };
};

export {
  fetchLensData,
  fetchLensContent,
  testLensWithClaude
};

export type {
  PromptTemplate,
  ToolSchema,
  TestLensParams,
  LensData
};
